/**
 * Offline Queue — IndexedDB-backed store for pending form submissions.
 *
 * Stores site visit creates/updates and checklist item creates when offline.
 * Items are processed in order by the sync handler (syncQueue.ts).
 */
import { openDB, type IDBPDatabase } from 'idb';

const DB_NAME = 'bg-project-tracker-offline';
const DB_VERSION = 2; // v2: consolidated — both offlineQueue + offlinePhotos stores created here
const STORE_NAME = 'offlineQueue';

export type OfflineAction =
  | 'create_visit'
  | 'update_visit'
  | 'create_checklist_item'
  | 'update_checklist_item';

export type OfflineStatus = 'pending' | 'syncing' | 'failed' | 'done';

export interface OfflineQueueEntry {
  /** Client-generated UUID; used to correlate photos + child items */
  id: string;
  action: OfflineAction;
  /** project_id for create_visit; null for other actions */
  project_id: string | null;
  /** Local UUID for creates; real server ID for updates */
  entity_id: string;
  /**
   * For checklist items: the visit entity_id (local UUID before sync,
   * replaced with real ID after parent visit syncs).
   */
  visit_id: string | null;
  /** Payload data for the API request */
  data: Record<string, unknown>;
  /** IDs of offlinePhotos entries associated with this queue item */
  photo_ids: string[];
  /** Unix ms timestamp when the action was queued */
  offline_timestamp: number;
  status: OfflineStatus;
  retry_count: number;
  error: string | null;
}

let _db: IDBPDatabase | null = null;

async function getDb(): Promise<IDBPDatabase> {
  if (_db) return _db;
  _db = await openDB(DB_NAME, DB_VERSION, {
    upgrade(db) {
      // offlineQueue store
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
        store.createIndex('status', 'status');
        store.createIndex('visit_id', 'visit_id');
        store.createIndex('offline_timestamp', 'offline_timestamp');
      }
      // offlinePhotos store — must be created in same DB version to avoid NotFoundError
      if (!db.objectStoreNames.contains('offlinePhotos')) {
        const photoStore = db.createObjectStore('offlinePhotos', { keyPath: 'id' });
        photoStore.createIndex('queue_entry_id', 'queue_entry_id');
        photoStore.createIndex('captured_at', 'captured_at');
      }
    },
  });
  return _db;
}

export const offlineQueue = {
  async add(entry: Omit<OfflineQueueEntry, 'retry_count' | 'error'>): Promise<void> {
    const db = await getDb();
    await db.add(STORE_NAME, { ...entry, retry_count: 0, error: null });
  },

  async getPending(): Promise<OfflineQueueEntry[]> {
    const db = await getDb();
    const all = await db.getAllFromIndex(STORE_NAME, 'status', 'pending');
    // Sort by offline_timestamp (oldest first)
    return all.sort((a, b) => a.offline_timestamp - b.offline_timestamp);
  },

  async getAll(): Promise<OfflineQueueEntry[]> {
    const db = await getDb();
    const all = await db.getAll(STORE_NAME);
    return all.sort((a, b) => a.offline_timestamp - b.offline_timestamp);
  },

  async setStatus(id: string, status: OfflineStatus): Promise<void> {
    const db = await getDb();
    const entry = await db.get(STORE_NAME, id);
    if (!entry) return;
    await db.put(STORE_NAME, { ...entry, status });
  },

  async recordFailure(id: string, error: string, retryCount: number): Promise<void> {
    const db = await getDb();
    const entry = await db.get(STORE_NAME, id);
    if (!entry) return;
    await db.put(STORE_NAME, {
      ...entry,
      status: retryCount >= 3 ? 'failed' : 'pending',
      error,
      retry_count: retryCount,
    });
  },

  /**
   * When a visit syncs, update child checklist items that used the local visit ID
   * to use the real server visit ID.
   */
  async updateChildVisitId(localVisitId: string, realVisitId: string): Promise<void> {
    const db = await getDb();
    const children = await db.getAllFromIndex(STORE_NAME, 'visit_id', localVisitId);
    for (const child of children) {
      await db.put(STORE_NAME, { ...child, visit_id: realVisitId });
    }
  },

  async delete(id: string): Promise<void> {
    const db = await getDb();
    await db.delete(STORE_NAME, id);
  },

  async clearDone(): Promise<void> {
    const db = await getDb();
    const done = await db.getAllFromIndex(STORE_NAME, 'status', 'done');
    for (const entry of done) {
      await db.delete(STORE_NAME, entry.id);
    }
  },

  async clearAll(): Promise<void> {
    const db = await getDb();
    await db.clear(STORE_NAME);
  },

  async count(): Promise<number> {
    const db = await getDb();
    return db.count(STORE_NAME);
  },

  async countPending(): Promise<number> {
    const db = await getDb();
    return db.countFromIndex(STORE_NAME, 'status', 'pending');
  },
};
